import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';

import { ConfigService } from '@ph-core/services/config/config.service';
import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { LoggerService } from '@ph-core/services/logger/logger.service';
import { RouterService } from '@ph-core/services/router/router.service';

import { AuthFlowTraceService } from '../services/auth/auth-flow-trace.service';
import { AuthService } from '../services/auth/auth.service';

/**
 * Authentication/session guard.
 * Determines whether the current user has an active application session and routes to
 * login/callback when the user is not authenticated.
 */
export const authGuard: CanActivateFn = (_: ActivatedRouteSnapshot, routerState: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const authFlowTrace = inject(AuthFlowTraceService);
  const logger = inject(LoggerService);
  const configService = inject(ConfigService);
  const routerService = inject(RouterService);

  authFlowTrace.record('guard-on-home-route', 'authGuard:activate', { targetUrl: routerState.url }, routerState.url);

  const redirectUnauthenticatedRequest = (): boolean => {
    localStorage.setItem('redirectUrl', routerState.url);
    const requiresOktaAuth = authService.isAuthenticationMechanismOkta();
    if (authService.isPostLogoutAllThisTab()) {
      void routerService.navigateToLogin();
    } else if (
      requiresOktaAuth &&
      configService.environment.features.unauthenticatedRedirectTarget === 'callback'
    ) {
      void routerService.navigateToCallback();
    } else {
      void routerService.navigateToLogin();
    }

    // Cancel the current attempted route transition while redirect navigation occurs.
    return false;
  };

  return authService
    .isAuthenticated()
    .then((isAuthenticated) => {
      void authService.getSessionAuthenticationDiagnostics('authGuard', routerState.url).then((diagnostics) => {
        logger.log(LoggerContext.Auth, 'authGuard session diagnostics', { isAuthenticated, diagnostics });
      });

      if (isAuthenticated) {
        authFlowTrace.record('guard-on-home-route', 'authGuard:allow', { targetUrl: routerState.url }, routerState.url);
        return true;
      }

      authFlowTrace.record('guard-on-home-route', 'authGuard:deny', { targetUrl: routerState.url }, routerState.url);

      return redirectUnauthenticatedRequest();
    })
    .catch((error: unknown) => {
      logger.log(LoggerContext.Auth, { authGuard: 'catch', error });
      try {
        return redirectUnauthenticatedRequest();
      } catch (redirectError) {
        logger.log(LoggerContext.Auth, { authGuard: 'redirect_failed', redirectError });

        return false;
      }
    });
};
